import React from 'react';
import { FaEdit } from 'react-icons/fa';
import { getFormatIcon } from './formatUtils';

type TemplateVariable = {
    name: string;
    display: string;
    field: string;
    format: string;
    type?: string;
    size?: number;
    properties?: Array<{
        name: string;
        display?: string;
        format: string;
        size?: number;
    }>;
    error?: string;
};

type TemplateVariableListProps = {
    variables: TemplateVariable[];
    onEdit: (variable: TemplateVariable, index: number) => void;
    emptyMessage?: string;
};

const TemplateVariableList: React.FC<TemplateVariableListProps> = ({
    variables,
    onEdit,
    emptyMessage = "Nenhuma variável detectada. Use {{nomeVariavel}} nos campos."
}) => {
    const getFormatLabel = (format: string, type?: string) => {
        if (type === 'array') return 'Lista';
        
        switch (format.toLowerCase()) {
            case 'email': return 'Email';
            case 'fileurl': return 'URL';
            case 'number': return 'Número';
            case 'currency': return 'Moeda';
            case 'date': return 'Data';
            case 'phone': return 'Telefone';
            case 'cnpj':
            case 'govtaxid': return 'CNPJ';
            case 'cpf':
            case 'socialid': return 'CPF';
            case 'json': return 'JSON';
            default: return 'Texto';
        }
    };

    if (variables.length === 0) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4">
                {emptyMessage}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {variables.map((variable, index) => (
                <div 
                    key={index} 
                    className={`p-3 rounded-lg border ${
                        variable.error 
                            ? 'bg-red-50 border-red-300 dark:bg-red-900 dark:border-red-700' 
                            : 'bg-gray-50 border-gray-200 dark:bg-slate-700 dark:border-slate-600'
                    }`}
                >
                    <div className="flex justify-between items-start gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                            <p className={`font-mono text-sm truncate ${
                                variable.error 
                                    ? 'text-red-600 dark:text-red-400' 
                                    : 'text-purple-600 dark:text-purple-400'
                            }`} title={`{{${variable.name}}}`}>
                                {`{{${variable.name}}}`}
                            </p>
                            
                            {!variable.error && (
                                <>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={variable.display}>
                                        {variable.display}
                                    </p>
                                    <div className="flex items-center gap-1 mt-1">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {getFormatIcon(variable.format, variable.type)}
                                        </span>
                                        <span className="text-xs text-gray-600 dark:text-gray-400">
                                            {getFormatLabel(variable.format, variable.type)}
                                        </span>
                                        {variable.type === 'email' && (
                                            <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">
                                                (formato fixo)
                                            </span>
                                        )}
                                    </div>
                                    
                                    {variable.properties && variable.properties.length > 0 && (
                                        <div className="mt-2 pl-3 border-l-2 border-purple-300 dark:border-purple-700">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Propriedades:</p>
                                            {variable.properties.map((prop) => (
                                                <div key={prop.name} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                                    <span className="text-gray-500 dark:text-gray-500">
                                                        {getFormatIcon(prop.format)}
                                                    </span>
                                                    <span className="font-mono">{prop.name}</span>
                                                    <span className="text-gray-500">({getFormatLabel(prop.format)})</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {variable.error && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                    {variable.error}
                                </p>
                            )}
                        </div>
                        
                        {!variable.error && (
                            <button
                                type="button"
                                onClick={() => onEdit(variable, index)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex-shrink-0"
                                title="Editar variável"
                            >
                                <FaEdit className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    
                    {!variable.error && (variable.format === 'text' || variable.format === 'number') && variable.size && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span>max: {variable.size}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default TemplateVariableList;
